/**
 * Tests for ConflictResolutionDialog component.
 *
 * Verifies comparison table rendering, formatValue logic,
 * and button callback invocations.
 */

import React from "react";
import { render, screen, fireEvent, within } from "@testing-library/react";
import "@testing-library/jest-dom";
import ConflictResolutionDialog from "./ConflictResolutionDialog";

describe("ConflictResolutionDialog", () => {
  const columns = [
    { field: "name", headerName: "Name" },
    { field: "email", headerName: "Email" },
    { field: "status", headerName: "Status" },
  ];

  const defaultProps = {
    open: true,
    onClose: jest.fn(),
    onKeepMine: jest.fn(),
    onUseTheirs: jest.fn(),
    yourValues: { name: "Alice", email: "alice@test.com", status: "active" },
    serverValues: { name: "Bob", email: "alice@test.com", status: "inactive" },
    columns,
  };

  beforeEach(() => {
    jest.clearAllMocks();
  });

  describe("Rendering", () => {
    it("renders dialog title", () => {
      render(<ConflictResolutionDialog {...defaultProps} />);
      expect(screen.getByText("Edit Conflict")).toBeInTheDocument();
    });

    it("renders explanation text", () => {
      render(<ConflictResolutionDialog {...defaultProps} />);
      expect(screen.getByText(/modified by another user/i)).toBeInTheDocument();
    });

    it("does not render when closed", () => {
      render(<ConflictResolutionDialog {...defaultProps} open={false} />);
      expect(screen.queryByText("Edit Conflict")).not.toBeInTheDocument();
    });
  });

  describe("Comparison Table", () => {
    it("only shows fields where values differ", () => {
      render(<ConflictResolutionDialog {...defaultProps} />);
      // name and status differ; email is the same
      expect(screen.getByText("Name")).toBeInTheDocument();
      expect(screen.getByText("Status")).toBeInTheDocument();
      // Email should NOT appear in the comparison since values match
      const table = screen.getByRole("table");
      expect(within(table).queryByText("Email")).not.toBeInTheDocument();
    });

    it("shows your values and server values", () => {
      render(<ConflictResolutionDialog {...defaultProps} />);
      expect(screen.getByText("Alice")).toBeInTheDocument();
      expect(screen.getByText("Bob")).toBeInTheDocument();
      expect(screen.getByText("active")).toBeInTheDocument();
      expect(screen.getByText("inactive")).toBeInTheDocument();
    });

    it("shows message when no visible differences", () => {
      const sameValues = {
        name: "Alice",
        email: "a@test.com",
        status: "active",
      };
      render(
        <ConflictResolutionDialog
          {...defaultProps}
          yourValues={sameValues}
          serverValues={sameValues}
        />,
      );
      expect(
        screen.getByText(/no visible field differences/i),
      ).toBeInTheDocument();
    });
  });

  describe("Value Formatting", () => {
    it("formats null as (empty)", () => {
      render(
        <ConflictResolutionDialog
          {...defaultProps}
          yourValues={{ name: null, email: "a", status: "a" }}
          serverValues={{ name: "Bob", email: "a", status: "a" }}
          columns={[{ field: "name", headerName: "Name" }]}
        />,
      );
      expect(screen.getByText("(empty)")).toBeInTheDocument();
    });

    it("formats boolean as Yes/No", () => {
      render(
        <ConflictResolutionDialog
          {...defaultProps}
          yourValues={{ name: true }}
          serverValues={{ name: false }}
          columns={[{ field: "name", headerName: "Name" }]}
        />,
      );
      expect(screen.getByText("Yes")).toBeInTheDocument();
      expect(screen.getByText("No")).toBeInTheDocument();
    });
  });

  describe("Button Actions", () => {
    it("calls onKeepMine with yourValues when clicked", () => {
      render(<ConflictResolutionDialog {...defaultProps} />);
      fireEvent.click(screen.getByRole("button", { name: /keep mine/i }));
      expect(defaultProps.onKeepMine).toHaveBeenCalledWith(
        defaultProps.yourValues,
      );
    });

    it("calls onUseTheirs when clicked", () => {
      render(<ConflictResolutionDialog {...defaultProps} />);
      fireEvent.click(screen.getByRole("button", { name: /use theirs/i }));
      expect(defaultProps.onUseTheirs).toHaveBeenCalledTimes(1);
    });

    it("calls onClose when Cancel is clicked", () => {
      render(<ConflictResolutionDialog {...defaultProps} />);
      fireEvent.click(screen.getByRole("button", { name: /cancel/i }));
      expect(defaultProps.onClose).toHaveBeenCalledTimes(1);
    });
  });
});
